import { HelpCircle } from 'lucide-react'
import { CSV_COLUMN_INFO, CSV_TEMPLATE_HEADERS } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const COLUMN_LABELS = {
  id: 'Employee ID',
  dob: 'Date of Birth',
  hire_date: 'Hire Date',
  termination_date: 'Termination Date',
  hours_served: 'Hours Served',
  prior_year_compensation: 'Prior-Year Compensation',
  cb_participant_entry_date: 'Cash Balance Entry Date',
  cb_opening_balance: 'Cash Balance Opening Balance',
  cb_prior_accrued_monthly_benefit: 'Prior Accrued Monthly Benefit',
  cb_prior_vesting_years: 'Prior Cash Balance Vesting Years',
  high_3_compensation: 'Highest 3-Year Average Compensation',
  section_415_compensation: 'Section 415 Compensation',
  pretax_percent: 'Pre-Tax Deferral Percent',
  pretax_amount: 'Pre-Tax Deferral Amount',
  roth_percent: 'Roth Deferral Percent',
  roth_amount: 'Roth Deferral Amount',
  loan_balance: 'Loan Balance',
  forfeiture_allocation: 'Forfeiture Allocation',
  qnec_amount: 'QNEC Amount',
  full_time: 'Full-Time Status',
  key_employee: 'Key Employee',
  five_percent_owner: 'More-Than-5% Owner',
  ownership_percent: 'Ownership Percent',
  family_of: 'Owner Family Attribution',
  allocation_group: 'Allocation Group',
  after_tax_percent: 'After-Tax Deferral Percent',
  after_tax_amount: 'After-Tax Deferral Amount',
}

const OPTIONAL_GROUPS = [
  {
    label: 'Employee & Employment',
    description: 'Identity, demographics, service, and employment status.',
    columns: ['id', 'name', 'gender', 'hire_date', 'tenure', 'termination_date', 'hours_served', 'status', 'full_time'],
  },
  {
    label: 'Pay & Savings',
    description: 'Compensation, deferrals, balances, and participant-level additions.',
    columns: [
      'prior_year_compensation',
      'section_415_compensation',
      'pretax_percent',
      'pretax_amount',
      'roth_percent',
      'roth_amount',
      'after_tax_percent',
      'after_tax_amount',
      'balance',
      'loan_balance',
      'forfeiture_allocation',
      'qnec_amount',
    ],
  },
  {
    label: 'Ownership & Plan Testing',
    description: 'Ownership, key-employee status, attribution, and allocation classes.',
    columns: ['key_employee', 'five_percent_owner', 'ownership_percent', 'family_of', 'allocation_group'],
  },
  {
    label: 'Cash Balance Details',
    description: 'Optional takeover and benefit-limit inputs for cash balance work.',
    columns: [
      'cb_participant_entry_date',
      'cb_opening_balance',
      'cb_prior_accrued_monthly_benefit',
      'cb_prior_vesting_years',
      'high_3_compensation',
    ],
  },
]

function labelForColumn(column) {
  return COLUMN_LABELS[column] || column
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function ColumnRow({ column }) {
  const info = CSV_COLUMN_INFO[column]
  if (!info) return null
  const label = labelForColumn(column)

  return (
    <div className="flex min-w-0 items-center gap-2 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground">{label}</p>
        <code
          translate="no"
          className="mt-0.5 block break-all font-mono text-[10px] leading-4 text-muted-foreground"
        >
          {column}
        </code>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About the ${label} column`}
            className="inline-flex h-8 w-8 shrink-0 touch-manipulation items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <HelpCircle className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="block max-w-sm space-y-1.5 leading-relaxed">
          <p className="font-semibold">{label}</p>
          <p>{info.description}</p>
          <p className="text-background/70">
            Example: <code translate="no" className="font-mono text-background">{info.example}</code>
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

function ColumnGroup({ label, description, columns, required = false }) {
  const availableColumns = columns.filter((column) => CSV_COLUMN_INFO[column])

  return (
    <section
      aria-labelledby={`csv-column-group-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`}
      className="min-w-0 rounded-lg border border-border bg-background px-3"
    >
      <div className="border-b border-border py-3">
        <div className="flex items-center justify-between gap-3">
          <h3
            id={`csv-column-group-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`}
            className="text-sm font-semibold text-foreground"
          >
            {label}
          </h3>
          {required && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
              Required
            </span>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="divide-y divide-border">
        {availableColumns.map((column) => <ColumnRow key={column} column={column} />)}
      </div>
    </section>
  )
}

export default function CsvColumnGuide({ id = 'column-guide', className }) {
  const requiredColumns = CSV_TEMPLATE_HEADERS.filter((column) => CSV_COLUMN_INFO[column]?.required)

  return (
    <TooltipProvider>
      <div id={id} className={cn('space-y-4', className)}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">CSV Column Guide</p>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-muted-foreground">
              Only Date of Birth and Compensation are required. Use the help buttons for accepted values and examples.
            </p>
          </div>
          <p className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {requiredColumns.length} required, {CSV_TEMPLATE_HEADERS.length - requiredColumns.length} optional
          </p>
        </div>

        <ColumnGroup
          label="Required Columns"
          description="Every imported row needs both values for plan calculations."
          columns={requiredColumns}
          required
        />

        <div>
          <h3 className="text-sm font-semibold text-foreground">Optional Columns</h3>
          <p className="mt-1 text-xs text-muted-foreground">Include only the fields available in the source file.</p>
          <div className="mt-3 grid items-start gap-3 md:grid-cols-2">
            {OPTIONAL_GROUPS.map((group) => (
              <ColumnGroup key={group.label} {...group} />
            ))}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
